import { EditorContextService } from "@magnolia/frontend-helpers-base";
import { environment } from "./environment";
import { wfetch } from "./utils/fetch";
import { getApiEndpoint } from "./utils/constants/api";
import { getAvailableLanguages, stripLanguageFromPathname } from "./utils/languages";
import { getUrlQueryParameters, extractQueryParameters, extractQueryParameter } from "./utils/url";
import { getFullPath } from "./utils/location";

export function getMagnoliaContext(location, availableLanguages = []) {
    const fullPath = getFullPath(location);

    return EditorContextService.getMagnoliaContext(fullPath, environment.APP_BASE, availableLanguages);
}

/**
 * Gets the node path from the URL, removing the language prefix.
 * @param {string[]} languages - Available language codes.
 * @param {string} rootNodePath - The root node path (e.g., '/home').
 * @returns {string} The node path without language prefix.
 */
function getNodePath(currentLanguageCode, fullPath, rootNodePath) {
    let pathname = fullPath;

    // Remove language prefix if present
    pathname = stripLanguageFromPathname(pathname, currentLanguageCode);

    // If pathname is just '/', return the rootNodePath
    if (pathname === "/") {
        return rootNodePath;
    }

    // If pathname doesn't start with rootNodePath, prepend it
    if (!pathname.startsWith(rootNodePath)) {
        return rootNodePath + pathname;
    }

    return pathname;
}

/**
 * Fetches the page content from Magnolia's delivery API.
 * @param {string} nodePath - The node path (without language prefix).
 * @param {string} currentLanguageCode - The current language code.
 * @returns {Promise<object|null>} The page content JSON or null on error.
 */
export async function fetchPageContent(nodePath, currentLanguageCode) {
    const urlParams = getUrlQueryParameters();
    const mgnlVersion = extractQueryParameter(urlParams, "mgnlVersion");
    const previewParams = extractQueryParameters(urlParams, ["mgnlPreview", "mgnlChannel"]);
    const queryParams = {
        lang: currentLanguageCode,
        ...(mgnlVersion && { version: mgnlVersion }),
        ...previewParams,
    };

    const response = await wfetch(getApiEndpoint("PAGES", { pathParams: { path: nodePath }, queryParams }));

    if (!response) return null;

    return response.data;
}

/**
 * Fetches the template annotations from Magnolia for the Page Editor.
 * @param {string} nodePath - The node path (without language prefix).
 * @param {boolean} isMagnolia - Whether we're in Magnolia editor context.
 * @returns {Promise<object>} The template annotations JSON or an empty object.
 */
async function fetchTemplateAnnotations(nodePath, isMagnolia) {
    if (!isMagnolia) return {};

    const urlParams = getUrlQueryParameters();
    const queryParams = extractQueryParameters(urlParams, ["mgnlVersion", "mgnlPreview", "mgnlChannel", "mgnlLocale"]);

    const response = await wfetch(getApiEndpoint("TEMPLATE_ANNOTATIONS", { pathParams: { path: nodePath }, queryParams }));

    if (!response) return {};

    return response.data;
}

/**
 * Main function to get all necessary data for a Magnolia page.
 * @param {string} fullPath - The current window location (pathname + search).
 * @param {object} siteData - The site data object.
 * @param {string} currentLanguageCode - The current language code.
 * @returns {Promise<object>} An object containing page, navigationContent, templateAnnotations, and magnoliaContext.
 */
export async function getMagnoliaProps(location, siteData, currentLanguageCode) {
    const availableLanguages = getAvailableLanguages(siteData); // Fetch available languages from site definition first

    // Get magnolia context from the library (used for EditablePage component)
    const magnoliaContext = getMagnoliaContext(location, availableLanguages);

    // Node path is built from pathname only — including location.search here
    // would leak query params (e.g. ?category=/foo) into the delivery URL,
    // producing a malformed `pages/...?category=...?lang=en` double-`?` URL.
    const nodePath = getNodePath(currentLanguageCode, location.pathname, environment.APP_BASE);
    const content = await fetchPageContent(nodePath, currentLanguageCode);
    const templateAnnotations = await fetchTemplateAnnotations(nodePath, magnoliaContext.isMagnolia);

    return {
        content,
        templateAnnotations,
        magnoliaContext,
    };
}
